import { z } from 'zod';

// RFC 7636 PKCE (S256) + OAuth `state` helpers for an OIDC Authorization
// Code flow that leaves the app via a full-page redirect and returns on a
// callback route. The sessionStorage stash carries the one-time secrets
// (code_verifier and state) across that round trip — sessionStorage
// because the values must survive the redirect in the SAME tab but never
// leak to other tabs or future sessions.
//
// Security contract: consumeSsoCallback removes the stash UNCONDITIONALLY
// before interpreting the URL (one-shot — a replayed or bookmarked callback
// URL can never reuse a verifier), and the returned `state` must equal the
// stashed one (CSRF guard: an authorization code injected into a browser
// that never started the flow fails as no_stash / state_mismatch).

const STASH_KEY = 'sanr-sso-pkce';

const StashSchema = z.looseObject({
  codeVerifier: z.string().min(1),
  state: z.string().min(1),
  returnTo: z.string().startsWith('/').optional(),
});

// Stryker disable next-line Regex: equivalent — btoa emits '=' only as trailing padding, so the unanchored match strips the same characters
const TRAILING_PADDING = /=+$/;

// Exported for its test alone (deterministic alphabet pins — the public
// entry only ever feeds it random bytes); not on the module barrel.
export function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(TRAILING_PADDING, '');
}

async function createCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier),
  );
  return base64Url(new Uint8Array(digest));
}

// Generates the PKCE pair and state, stashes the secrets for the callback
// leg, and returns the fully-built `${issuer}/oauth/authorize` URL for a
// top-level navigation. Calling again before the round trip completes
// overwrites the stash — the newest redirect wins, older callback URLs then
// fail the state check by construction.
export async function beginSsoRedirect({
  issuer,
  clientId,
  scopes,
  redirectUri,
  returnTo,
  prompt,
}: {
  issuer: string;
  clientId: string;
  scopes: readonly string[];
  redirectUri: string;
  // App-internal path ('/…', never a full URL — the schema fails anything
  // else closed) for the consumer to navigate back to once the callback
  // completes; rides the stash across the redirect round trip.
  returnTo?: string;
  // OIDC prompt for the authorize request. 'login' forces the issuer's
  // credentials form; 'select_account' forces the issuer's account
  // chooser only while its session holds several accounts — a lone one
  // it resumes silently, so adding a second account needs 'login';
  // omitted, the issuer's default (silent resume when a session
  // exists) applies. The type admits only values the Santiment issuer
  // accepts — its prompt whitelist is none/login/consent/select_account
  // and anything else is rejected with a 400 (select_account accepted
  // since 2026-08-26; earlier it 400d too — verified live on staging).
  prompt?: 'login' | 'select_account';
}): Promise<string> {
  const codeVerifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = base64Url(crypto.getRandomValues(new Uint8Array(16)));
  sessionStorage.setItem(
    STASH_KEY,
    JSON.stringify({ codeVerifier, state, returnTo }),
  );
  const url = new URL('/oauth/authorize', issuer);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', scopes.join(' '));
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  if (prompt !== undefined) {
    url.searchParams.set('prompt', prompt);
  }
  return url.toString();
}

export type SsoCallbackResult =
  | {
      status: 'ok';
      code: string;
      // unread-field-allow: consumed structurally — the ok result passes whole into the login exchange's {code, codeVerifier} param
      codeVerifier: string;
      returnTo?: string;
    }
  | {
      status: 'error';
      reason: 'sso_error' | 'no_stash' | 'state_mismatch' | 'missing_code';
    };

// Interprets the callback URL's query string against the stash, consuming
// the stash (one-shot). Precedence: an explicit SSO `error` param wins over
// every local check (it is the SSO host's own refusal — user denied,
// expired login page); then a missing/corrupt stash, a state mismatch, and
// a missing code each fail closed.
export function consumeSsoCallback(search: string): SsoCallbackResult {
  const raw = sessionStorage.getItem(STASH_KEY);
  sessionStorage.removeItem(STASH_KEY);
  const params = new URLSearchParams(search);
  if (params.get('error') != null)
    return { status: 'error', reason: 'sso_error' };
  // Stryker disable next-line ConditionalExpression: equivalent — with the guard gone, JSON.parse(null) yields null and the schema parse throws into the same no_stash; the guard exists for the JSON.parse(string) type
  if (raw === null) return { status: 'error', reason: 'no_stash' };
  let stash: z.infer<typeof StashSchema>;
  try {
    stash = StashSchema.parse(JSON.parse(raw));
  } catch {
    return { status: 'error', reason: 'no_stash' };
  }
  if (params.get('state') !== stash.state) {
    return { status: 'error', reason: 'state_mismatch' };
  }
  const code = params.get('code');
  if (code == null || code === '') {
    return { status: 'error', reason: 'missing_code' };
  }
  return {
    status: 'ok',
    code,
    codeVerifier: stash.codeVerifier,
    ...(stash.returnTo !== undefined && { returnTo: stash.returnTo }),
  };
}

// The account-switch redirect leg differs from the login one: the
// backend built the authorize URL, so the PKCE verifier and the OAuth
// state are SERVER-held — the client stashes only enough to recognize the
// return as the expected switch and route it past the login handling:
// the state parsed out of the redirect URL, and where to return in-app.
// Separate key from the PKCE stash so a concurrent login redirect can't
// clobber it.
const SWITCH_STASH_KEY = 'sanr-sso-switch';

const SwitchStashSchema = z.looseObject({
  state: z.string().min(1),
  returnTo: z.string().startsWith('/').optional(),
});

// Stashes the switch-return expectation before the caller top-level
// navigates to redirectUrl. Throws when redirectUrl carries no state param
// — without it the return leg is unrecognizable, so navigating would
// strand the user on the callback's error path; the caller must not
// navigate. Re-calling overwrites the stash — the newest switch wins.
export function beginSsoSwitch({
  redirectUrl,
  returnTo,
}: {
  redirectUrl: string;
  // App-internal path ('/…') to land on after the callback completes; the
  // schema fails anything else closed on the consume side.
  returnTo?: string;
}): void {
  const state = new URL(redirectUrl).searchParams.get('state');
  if (state == null || state === '') {
    throw new Error('switch redirectUrl carries no state');
  }
  sessionStorage.setItem(SWITCH_STASH_KEY, JSON.stringify({ state, returnTo }));
}

export type SsoSwitchCallbackResult =
  | { status: 'ok'; code: string; state: string; returnTo?: string }
  | {
      status: 'error';
      reason: 'sso_error' | 'missing_code';
      returnTo?: string;
    };

// Interprets the callback URL as a possible switch return. null means "not
// the expected switch return" — no stash, corrupt stash, or a state that
// doesn't match — and the caller falls through to the login callback
// handling (which fails closed on its own stash). The stash is removed on
// every non-null read of it (one-shot, like the PKCE stash); an abandoned
// switch's stale marker is dropped by the next callback visit. 'sso_error'
// is the SSO host's own refusal on OUR state — with prompt=none that means
// silent auth needs interaction (login_required and friends).
export function consumeSsoSwitchCallback(
  search: string,
): SsoSwitchCallbackResult | null {
  const raw = sessionStorage.getItem(SWITCH_STASH_KEY);
  // Stryker disable next-line ConditionalExpression: equivalent — with the guard gone, JSON.parse(null) yields null and the schema parse throws into the same null return; the guard exists for the JSON.parse(string) type
  if (raw === null) return null;
  sessionStorage.removeItem(SWITCH_STASH_KEY);
  let stash: z.infer<typeof SwitchStashSchema>;
  try {
    stash = SwitchStashSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
  const params = new URLSearchParams(search);
  if (params.get('state') !== stash.state) return null;
  const returnTo =
    stash.returnTo !== undefined ? { returnTo: stash.returnTo } : {};
  if (params.get('error') != null) {
    return { status: 'error', reason: 'sso_error', ...returnTo };
  }
  const code = params.get('code');
  if (code == null || code === '') {
    return { status: 'error', reason: 'missing_code', ...returnTo };
  }
  return { status: 'ok', code, state: stash.state, ...returnTo };
}
